import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { API_URL } from '../constants/urls';

export interface CompanySegment {
  id: string;
  name: string;
}

export interface Company {
  id: string;
  organization_id: string;
  name: string;
  company_type?: string;
  status?: string; // suspect, target, prospect, customer, former_customer, lost
  strategic_owner_id?: string;
  strategic_owner_name?: string;
  website?: string;
  linkedin_url?: string;
  description?: string;
  employee_count?: number;
  annual_revenue?: number;
  revenue_tier?: string;
  notes?: string;
  segments?: CompanySegment[];

  // Aggregated metrics
  booked_business?: number;
  lost_business?: number;
  open_pipeline_unweighted?: number;
  open_pipeline_weighted?: number;

  created_at: string;
  updated_at: string;
}

export const companiesService = {
  async list(organizationId: string): Promise<Company[]> {
    try {
      const response = await axios.get(
        `${API_URL}/companies?organization_id=${organizationId}`,
        {
          headers: getAuthHeaders(),
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching companies:', error);
      throw error;
    }
  },

  async get(companyId: string, organizationId: string): Promise<Company> {
    try {
      const response = await axios.get(
        `${API_URL}/companies/${companyId}?organization_id=${organizationId}`,
        {
          headers: getAuthHeaders(),
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching company:', error);
      throw error;
    }
  },

  async create(
    company: Partial<Company>,
    organizationId: string,
  ): Promise<Company> {
    try {
      const response = await axios.post(
        `${API_URL}/companies?organization_id=${organizationId}`,
        company,
        {
          headers: getAuthHeaders(),
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error creating company:', error);
      throw error;
    }
  },

  async update(
    companyId: string,
    updates: Partial<Company>,
    organizationId: string,
  ): Promise<Company> {
    try {
      const response = await axios.put(
        `${API_URL}/companies/${companyId}?organization_id=${organizationId}`,
        updates,
        {
          headers: getAuthHeaders(),
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error updating company:', error);
      throw error;
    }
  },

  async delete(companyId: string, organizationId: string): Promise<void> {
    try {
      await axios.delete(
        `${API_URL}/companies/${companyId}?organization_id=${organizationId}`,
        {
          headers: getAuthHeaders(),
        },
      );
    } catch (error) {
      console.error('Error deleting company:', error);
      throw error;
    }
  },
};
